/*
 * Elexa A4: product-info block behaviour (loaded deferred by the blocks that need it; safe to
 * include more than once). Uses window.Elexa (elexa-core.js) and Dawn's pub/sub when present.
 *
 *   [data-ex-pbi-anchor]   smooth scroll to a section id (sticky-header offset, reduced motion)
 *   [data-ex-pbi-swap]     re-rendered on variant change (limited inventory alert)
 *   <elexa-pbi-tabs>       ARIA tabs (click, Left/Right, Home/End, roving tabindex)
 *   [data-ex-sc-opener]    size chart: removes stale copies of a moved <modal-dialog>
 *   [data-ex-dt-copy]      deal timer copy-code button (+ optional Elexa.cart.applyDiscount)
 *   [data-ex-sof]          selling-out-fast fill animation when scrolled into view
 */
(() => {
  const Elexa = (window.Elexa = window.Elexa || {});
  if (Elexa.pbInfo) return;
  Elexa.pbInfo = { version: '1.0.0' };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = () => (Elexa.isDesignMode ? Elexa.isDesignMode() : Boolean(window.Shopify && window.Shopify.designMode));

  /* ---------------------------------------------------------------- *
   * Anchor links to the reviews (or any) section
   * ---------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-ex-pbi-anchor]');
    if (!link) return;
    event.preventDefault();
    const id = link.getAttribute('data-ex-pbi-anchor');
    const target = id ? document.getElementById(id) : null;
    if (!target) return;
    if (Elexa.scrollTo) {
      Elexa.scrollTo(target);
    } else {
      target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  /* ---------------------------------------------------------------- *
   * Variant change: swap [data-ex-pbi-swap] elements from fresh HTML
   * ---------------------------------------------------------------- */
  const swapFrom = (doc, sectionId) => {
    if (!doc) return;
    document.querySelectorAll('[data-ex-pbi-swap]').forEach((el) => {
      if (sectionId && el.dataset.exSection && el.dataset.exSection !== String(sectionId)) return;
      const fresh = doc.getElementById(el.id);
      if (fresh && fresh !== el) el.replaceWith(document.importNode(fresh, true));
    });
  };

  // Dawn's product-info publishes the re-rendered section HTML with every variant change.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event && event.data;
      if (data && data.html) swapFrom(data.html, data.sectionId);
    });
  }

  // Contract event (A1). If nothing swapped for this variant, fetch the section once.
  const pending = new Map();
  document.addEventListener('elexa:variant-change', (event) => {
    const detail = event.detail || {};
    const variant = detail.variant;
    if (!variant || !variant.id) return;
    const key = String(detail.sectionId || '');
    clearTimeout(pending.get(key));
    pending.set(
      key,
      setTimeout(() => {
        const stale = Array.from(document.querySelectorAll('[data-ex-pbi-swap]')).filter(
          (el) => (!key || el.dataset.exSection === key) && el.dataset.variantId !== String(variant.id)
        );
        if (!stale.length) return;
        const first = stale[0];
        const url = (first.dataset.url || window.location.pathname).split('?')[0];
        const sectionId = first.dataset.exSection;
        fetch(`${url}?variant=${encodeURIComponent(variant.id)}&section_id=${encodeURIComponent(sectionId)}`)
          .then((response) => response.text())
          .then((text) => swapFrom(new DOMParser().parseFromString(text, 'text/html'), sectionId))
          .catch(() => {});
      }, 150)
    );
  });

  /* ---------------------------------------------------------------- *
   * Product tabs
   * ---------------------------------------------------------------- */
  class ElexaPbiTabs extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.tabs().includes(tab)) this.select(tab, false);
      });
      this.addEventListener('keydown', (event) => this.onKey(event));
      this.mark();
    }

    tabs() {
      return Array.from(this.querySelectorAll('[role="tab"]')).filter((t) => t.closest('elexa-pbi-tabs') === this);
    }

    onKey(event) {
      const tabs = this.tabs();
      const index = tabs.indexOf(document.activeElement);
      if (index < 0) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const step = (dir) => tabs[(index + dir + tabs.length) % tabs.length];
      let next = null;
      switch (event.key) {
        case 'ArrowRight':
          next = step(rtl ? -1 : 1);
          break;
        case 'ArrowLeft':
          next = step(rtl ? 1 : -1);
          break;
        case 'Home':
          next = tabs[0];
          break;
        case 'End':
          next = tabs[tabs.length - 1];
          break;
        default:
          return;
      }
      event.preventDefault();
      this.select(next, true);
    }

    select(tab, focus) {
      this.tabs().forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus({ preventScroll: true });
      const bar = tab.parentElement;
      if (bar && bar.scrollWidth > bar.clientWidth + 1) {
        const left = tab.offsetLeft - bar.offsetLeft;
        if (left < bar.scrollLeft || left + tab.offsetWidth > bar.scrollLeft + bar.clientWidth) {
          bar.scrollTo({ left: left - (bar.clientWidth - tab.offsetWidth) / 2, behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      }
      this.mark();
    }

    mark() {
      const tabs = this.tabs();
      tabs.forEach((t, i) => {
        const nextTab = tabs[i + 1];
        t.classList.toggle('is-before-active', Boolean(nextTab && nextTab.getAttribute('aria-selected') === 'true'));
      });
    }
  }
  if (!customElements.get('elexa-pbi-tabs')) customElements.define('elexa-pbi-tabs', ElexaPbiTabs);

  /* ---------------------------------------------------------------- *
   * Size chart: Dawn's <modal-dialog> moves itself to <body>. When the
   * product section is re-rendered a second copy can appear; keep the newest.
   * ---------------------------------------------------------------- */
  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest('[data-ex-sc-opener]');
      if (!opener) return;
      const copies = document.querySelectorAll(`[id="ExSizeChart-${opener.dataset.exScOpener}"]`);
      for (let i = 0; i < copies.length - 1; i += 1) copies[i].remove();
    },
    true
  );

  /* ---------------------------------------------------------------- *
   * Deal timer: copy code (and optionally apply it to the cart)
   * ---------------------------------------------------------------- */
  const selectText = (node) => {
    if (!node || !window.getSelection) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ex-dt-copy]');
    if (!button) return;
    event.preventDefault();
    const code = button.dataset.code || '';
    if (!code) return;
    const live = button.parentElement && button.parentElement.querySelector('[data-ex-dt-live]');
    let ok = false;
    if (Elexa.copy) {
      ok = await Elexa.copy(code, button);
    } else {
      try {
        await navigator.clipboard.writeText(code);
        ok = true;
      } catch (error) {
        ok = false;
      }
      button.setAttribute('data-copied', ok ? 'true' : 'false');
      clearTimeout(button.exCopyTimer);
      button.exCopyTimer = setTimeout(() => button.removeAttribute('data-copied'), 2000);
    }
    if (!ok) selectText(button.querySelector('[data-ex-dt-code]'));
    if (live) {
      const copied = button.querySelector('.ex-dt__btn-copied');
      live.textContent = ok && copied ? copied.textContent.trim() : code;
      clearTimeout(live.exTimer);
      live.exTimer = setTimeout(() => (live.textContent = ''), 2000);
    }
    if (button.dataset.apply === 'true' && Elexa.cart && typeof Elexa.cart.applyDiscount === 'function') {
      Elexa.cart.applyDiscount(code, { source: 'deal-timer' }).catch(() => {});
    }
  });

  /* ---------------------------------------------------------------- *
   * Selling out fast: animate fills from 0 when scrolled into view
   * ---------------------------------------------------------------- */
  const initSof = (root) => {
    if (root.hasAttribute('data-sof-ready') || reducedMotion() || designMode() || !('IntersectionObserver' in window)) return;
    const rect = root.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) return; // already on screen: no flash
    root.setAttribute('data-sof-ready', '');
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-visible')));
      },
      { threshold: 0.2 }
    );
    observer.observe(root);
  };
  const scanSof = (scope) => (scope || document).querySelectorAll('[data-ex-sof]').forEach(initSof);
  scanSof(document);
  document.addEventListener('shopify:section:load', (event) => scanSof(event.target));
})();
